import { MenuLinks } from '@/constants/menu'
import { DashboardChartEmpty } from '@/pages/dashboard/components/DashboardChartEmpty'
import { DashboardDateFilter } from '@/pages/dashboard/components/DashboardDateFilter'
import { DashboardKpiGrid } from '@/pages/dashboard/components/DashboardKpiGrid'
import { DashboardSection } from '@/pages/dashboard/components/DashboardSection'
import { DashboardChartHost } from '@/pages/dashboard/components/DashboardChartHost'
import { VolumeRevenueTrendChart } from '@/pages/dashboard/components/VolumeRevenueTrendChart'
import { baseChartOptions, chartPanel, chartTheme, STATUS_COLORS } from '@/pages/dashboard/components/dashboardTheme'
import { useDateRangeFilter } from '@/pages/dashboard/hooks/useDateRangeFilter'
import { toApiDateRange } from '@/pages/dashboard/utils/dateRangeUtils'
import { buildVolumeRevenueTrendPoints, formatMoney } from '@/pages/dashboard/utils/transactionChartUtils'
import { transactionAdminApi } from '@/services/transactionAdminApi'
import type { AdminTransactionListResponse, TransactionAnalyticsSummaryResponse } from '@/services/transactionTypes'
import type { ApexOptions } from 'apexcharts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'

export function TransactionDashboardSection() {
	const dateFilter = useDateRangeFilter('30')
	const [loading, setLoading] = useState(true)
	const [analytics, setAnalytics] = useState<TransactionAnalyticsSummaryResponse | null>(null)
	const [rows, setRows] = useState<AdminTransactionListResponse[]>([])

	const loadTransactions = useCallback(async (range: { dateFrom: string; dateTo: string }) => {
		setLoading(true)
		try {
			const apiDates = toApiDateRange(range.dateFrom, range.dateTo)
			const request = {
				pageNumber: 1,
				pageSize: 1000,
				sortField: 'createdOn',
				sortOrder: 'desc',
				...apiDates,
			}
			const [summary, list] = await Promise.all([
				transactionAdminApi.getAnalyticsSummary(request),
				transactionAdminApi.search(request),
			])
			setAnalytics(summary)
			setRows(list.data ?? [])
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		loadTransactions(dateFilter.appliedRange)
	}, [dateFilter.appliedRange, loadTransactions])

	const trendPoints = useMemo(
		() => buildVolumeRevenueTrendPoints(rows, dateFilter.appliedRange.dateFrom, dateFilter.appliedRange.dateTo),
		[rows, dateFilter.appliedRange]
	)

	const statusDonut = useMemo(() => {
		if (!analytics || analytics.totalCount === 0) return null
		return [
			{ label: 'Succeeded', value: analytics.succeededCount, color: STATUS_COLORS.Succeeded },
			{ label: 'Failed', value: analytics.failedCount, color: STATUS_COLORS.Failed },
			{ label: 'Pending', value: analytics.pendingCount, color: STATUS_COLORS.Pending },
			{ label: 'Refunded', value: analytics.refundedCount, color: STATUS_COLORS.Refunded },
		].filter((s) => s.value > 0)
	}, [analytics])

	const donutOptions: ApexOptions = useMemo(
		() => ({
			chart: { ...baseChartOptions, type: 'donut' },
			theme: { mode: 'dark' },
			labels: statusDonut?.map((s) => s.label) ?? [],
			colors: statusDonut?.map((s) => s.color) ?? [],
			stroke: { width: 0 },
			dataLabels: { enabled: false },
			legend: { position: 'bottom', fontSize: '12px', labels: { colors: chartTheme.foreColor } },
			plotOptions: {
				pie: {
					donut: {
						size: '72%',
						labels: {
							show: true,
							total: {
								show: true,
								label: 'Total',
								color: chartTheme.foreColor,
								formatter: () => (analytics?.totalCount ?? 0).toLocaleString(),
							},
						},
					},
				},
			},
			tooltip: { theme: 'dark', y: { formatter: (v) => `${Math.round(v ?? 0)} payments` } },
		}),
		[statusDonut, analytics?.totalCount]
	)

	const kpis = analytics
		? [
				{
					label: 'Total revenue',
					value: formatMoney(analytics.succeededAmount, analytics.currency),
					hint: `${analytics.succeededCount} succeeded`,
					icon: 'ri-money-dollar-circle-line',
					accentClass: 'text-[#1ed27d]',
				},
				{
					label: 'Transactions',
					value: analytics.totalCount.toLocaleString(),
					hint: `${analytics.pendingCount} pending`,
					icon: 'ri-exchange-line',
					accentClass: 'text-primary',
				},
				{
					label: 'Success rate',
					value: analytics.totalCount > 0 ? `${Math.round((analytics.succeededCount / analytics.totalCount) * 100)}%` : '—',
					hint: `${analytics.failedCount} failed`,
					icon: 'ri-checkbox-circle-line',
					accentClass: 'text-success',
				},
				{
					label: 'Avg. order value',
					value:
						analytics.succeededCount > 0
							? formatMoney(analytics.succeededAmount / analytics.succeededCount, analytics.currency)
							: '—',
					hint: `${analytics.refundedCount} refunded`,
					icon: 'ri-pie-chart-2-line',
					accentClass: 'text-info',
				},
			]
		: []

	return (
		<DashboardSection
			title="Transactions & revenue"
			subtitle={dateFilter.rangeLabel}
			icon="ri-bank-card-line"
			analyticsLink={MenuLinks.Transactions}
			filter={
				<DashboardDateFilter
					preset={dateFilter.preset}
					dateFrom={dateFilter.dateFrom}
					dateTo={dateFilter.dateTo}
					onPresetClick={dateFilter.onPresetClick}
					onDateFromChange={dateFilter.setDateFrom}
					onDateToChange={dateFilter.setDateTo}
					onApply={dateFilter.onApplyRange}
				/>
			}
		>
			{analytics ? <DashboardKpiGrid items={kpis} /> : null}

			<div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1.65fr)_minmax(280px,1fr)]">
				<div className={chartPanel}>
					<div className="mb-4 flex items-start justify-between gap-3">
						<div>
							<h3 className="text-sm font-semibold text-foreground">Volume & revenue trend</h3>
							<p className="mt-0.5 text-xs text-muted-foreground">Daily transaction count and succeeded revenue</p>
						</div>
					</div>
					<VolumeRevenueTrendChart
						points={trendPoints}
						currency={analytics?.currency ?? 'usd'}
						loading={loading}
					/>
				</div>

				<div className={chartPanel}>
					<h3 className="mb-1 text-sm font-semibold text-foreground">Payment status</h3>
					<p className="mb-3 text-xs text-muted-foreground">Share by outcome</p>
					{loading ? (
						<div className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">Loading chart…</div>
					) : statusDonut && statusDonut.length > 0 ? (
						<DashboardChartHost>
							<ReactApexChart
								options={donutOptions}
								series={statusDonut.map((s) => s.value)}
								type="donut"
								height={300}
								width="100%"
							/>
						</DashboardChartHost>
					) : (
						<DashboardChartEmpty message="No status data for the selected period." />
					)}
				</div>
			</div>
		</DashboardSection>
	)
}
